// Superbalk: een balk helemaal bovenaan, alleen voor platformbeheer, op elke
// omgeving. Hij geeft:
//
//   - de omgeving in kleur (PRODUCTIE rood, STAGING oranje), zodat je nooit
//     denkt dat je op staging zit terwijl je op productie werkt
//   - snel wisselen: startscherm, platformbeheer, elke organisatie
//   - bekijk als: de app zoals een owner, admin, editor of viewer hem ziet
//   - inzicht: je echte rol, de bekeken rol, modules, rechten, toegang
//
// Hij geeft geen extra rechten. Voor gegevens van een organisatie waar je geen
// lid van bent, vraag je platformtoegang aan (tijdelijk, met reden, in het log).
// De database blijft het slot.
import { APP_ENV, SUPABASE_URL } from '../config.js';
import { t, koppelTaalkeuze } from '../lib/i18n.js';
import { esc } from '../lib/format.js';
import { toast } from './toast.js';
import { state } from '../state.js';
import * as router from '../router.js';
import * as context from '../lib/context.js';

const KLEUR = { production: 'rood', staging: 'oranje', preview: 'paars', development: 'grijs' };
let sb = null;
let email = '';
let contexten = [];

const project = () => { try { return new URL(SUPABASE_URL).hostname.split('.')[0]; } catch { return '?'; } };

export async function toon(client, gegevens) {
  sb = client;
  email = String(gegevens.email || '').toLowerCase();
  let el = document.getElementById('superbalk');
  if (!el) {
    el = document.createElement('div');
    el.id = 'superbalk';
    el.className = 'superbalk';
    document.body.prepend(el);
    el.addEventListener('click', klik);
    el.addEventListener('change', wijzig);
  }
  document.body.classList.add('met-superbalk');
  const { data } = await sb.rpc('mijn_contexten');
  contexten = data || [];
  teken();
}

// Opnieuw tekenen, bijvoorbeeld nadat de app de organisatie heeft geladen.
export function ververs() { if (document.getElementById('superbalk')) teken(); }

function teken() {
  const el = document.getElementById('superbalk');
  const ctx = context.gekozen();
  const sim = context.simulatie()?.rol || '';
  const env = APP_ENV || 'production';
  const huidig = ctx?.soort === 'org' ? ctx.org : (ctx?.soort === 'platform' ? '__platform' : ctx?.soort === 'start' ? '__start' : state.currentOrgId || '');
  el.innerHTML = `
    <span class="superbalk-env superbalk-${KLEUR[env] || 'grijs'}" title="${esc(project())}">${esc(t('super.env.' + env))}</span>
    <span class="superbalk-titel"><i class="ti ti-bolt"></i> ${esc(t('super.titel'))}</span>
    <label class="superbalk-veld">${esc(t('super.naar'))}
      <select data-super="naar">
        <option value="__start" ${huidig === '__start' ? 'selected' : ''}>${esc(t('super.start'))}</option>
        <option value="__platform" ${huidig === '__platform' ? 'selected' : ''}>${esc(t('super.platform'))}</option>
        ${contexten.map(o => `<option value="${o.organisation_id}" ${huidig === o.organisation_id ? 'selected' : ''}>${esc(o.naam)}${o.lid ? '' : o.toegang_tot ? ' · ' + esc(t('super.toegang')) : ' · ' + esc(t('super.geentoegang'))}</option>`).join('')}
      </select></label>
    <label class="superbalk-veld">${esc(t('super.bekijkals'))}
      <select data-super="rol">
        <option value="">${esc(t('super.eigenrol'))}</option>
        ${router.systeemrollen().slice().reverse().map(r => `<option value="${r}" ${sim === r ? 'selected' : ''}>${esc(t('start.rolnaam.' + r))}</option>`).join('')}
      </select></label>
    <label class="superbalk-veld"><i class="ti ti-language"></i><select data-taalkeuze aria-label="Taal / Language"></select></label>
    ${sim ? `<span class="superbalk-let"><i class="ti ti-eye"></i> ${esc(t('super.simulatie', { rol: t('start.rolnaam.' + sim) }))}</span>` : ''}
    <button type="button" class="superbalk-knop" data-super="inzicht"><i class="ti ti-info-circle"></i> ${esc(t('super.inzicht'))}</button>
    <div class="superbalk-paneel" id="superbalkPaneel" hidden></div>
    <div class="superbalk-paneel" id="superbalkAanvraag" hidden></div>`;
  koppelTaalkeuze(el);
}

function inzicht() {
  const org = contexten.find(o => o.organisation_id === state.currentOrgId);
  const mods = router.modules();
  const rij = (label, waarde) => `<div class="superbalk-rij"><span>${esc(label)}</span><span>${waarde}</span></div>`;
  return `
    ${rij(t('super.i.email'), esc(email))}
    ${rij(t('super.i.omgeving'), esc(`${APP_ENV} · ${project()}`))}
    ${rij(t('super.i.organisatie'), org ? `${esc(org.naam)} <span class="superbalk-zacht">${esc((org.types || []).join(', '))}</span>` : '&ndash;')}
    ${rij(t('super.i.echterol'), org ? esc(org.lid ? t('start.rolnaam.' + org.rol) : t('super.i.viatoegang')) : '&ndash;')}
    ${rij(t('super.i.bekeken'), esc(state.currentRole ? t('start.rolnaam.' + state.currentRole) : '–'))}
    ${rij(t('super.i.toegang'), org?.toegang_tot ? esc(new Date(org.toegang_tot).toLocaleString()) : '&ndash;')}
    ${rij(t('super.i.modules'), mods ? esc(mods.join(', ') || '–') : esc(t('super.i.onbekend')))}
    ${rij(t('super.i.rechten'), `<span class="superbalk-zacht">${esc(router.allePermissies().join(', ') || '–')}</span>`)}
    <p class="superbalk-zacht" style="margin-top:6px">${esc(t('super.i.uitleg'))}</p>`;
}

async function wijzig(e) {
  const s = e.target.closest('[data-super]');
  if (!s) return;
  if (s.dataset.super === 'rol') { context.zetSimulatie(s.value || null); return; }
  if (s.dataset.super !== 'naar') return;
  const v = s.value;
  if (v === '__start') return context.naarStart();
  if (v === '__platform') return context.kies({ soort: 'platform' }, 'platform-dash');
  const org = contexten.find(o => o.organisation_id === v);
  if (org?.lid || org?.toegang_tot) return context.kies({ soort: 'org', org: v });
  // Geen lid en geen toegang: eerst toegang aanvragen, met reden.
  const p = document.getElementById('superbalkAanvraag');
  p.hidden = false;
  p.innerHTML = `<div style="font-size:13px;margin-bottom:6px">${esc(t('super.aanvraag', { naam: org?.naam || '' }))}</div>
    <div style="display:flex;gap:6px;flex-wrap:wrap">
      <input class="form-input" data-super="reden" placeholder="${esc(t('start.toegang.voorbeeld'))}" style="flex:1;min-width:220px">
      <select class="form-input" data-super="uren" style="width:auto">${[1, 2, 4, 8].map(u => `<option value="${u}" ${u === 4 ? 'selected' : ''}>${u} ${esc(t('super.uur'))}</option>`).join('')}</select>
      <button type="button" class="btn btn-primary btn-sm" data-super="aanvragen" data-org="${v}"><i class="ti ti-key"></i> ${esc(t('start.toegang.aanvragen'))}</button>
      <button type="button" class="btn btn-ghost btn-sm" data-super="annuleer">${esc(t('alg.annuleren'))}</button>
    </div>`;
}

async function klik(e) {
  const k = e.target.closest('button[data-super]');
  if (!k) return;
  const a = k.dataset.super;
  if (a === 'inzicht') {
    const p = document.getElementById('superbalkPaneel');
    p.hidden = !p.hidden;
    if (!p.hidden) p.innerHTML = inzicht();
  }
  if (a === 'annuleer') { document.getElementById('superbalkAanvraag').hidden = true; teken(); }
  if (a === 'aanvragen') {
    const p = document.getElementById('superbalkAanvraag');
    const reden = p.querySelector('[data-super="reden"]').value.trim();
    const uren = Number(p.querySelector('[data-super="uren"]').value) || 4;
    if (reden.length < 10) { toast(t('start.toegang.kort'), 'error'); return; }
    const nu = new Date();
    const { error } = await sb.from('platform_access_grants').insert([{
      organisation_id: k.dataset.org, email, reason: reden,
      granted_at: nu.toISOString(), expires_at: new Date(nu.getTime() + uren * 3600 * 1000).toISOString(),
    }]);
    if (error) { toast(t('alg.fout', { melding: error.message }), 'error'); return; }
    context.kies({ soort: 'org', org: k.dataset.org });
  }
}
